/* Copyright (c) 2017 Backpack Health LLC */

/* global window */
import React from 'react';
import * as _ from 'lodash';
import { injectIntl } from 'react-intl';

import './ShareCardEdit.styles.scss';

import ShareCardBottomBar from '../components/ShareCardBottomBar';
import  { EditableShareCardHeaderTitle } from '../components/ShareCardHeaderTitle';
import ShareCardTemplate from '../components/ShareCardTemplate';
import ShareCardSection from '../components/ShareCardSection';
import ViewShareCardWallpaperSetup from '../shareCardSummary/ViewShareCardWallpaperSetup';
import TrashShareCardButton from './TrashShareCardButton';
import { ShareCardCategory } from './ShareCardCategory';
import ShareCardCommentRow from './ShareCardCommentRow';
import ColorSelector from './ColorSelector';
import ShareCardProfileGroup from './ShareCardProfileGroup';
import ShareCardGroupContents from './ShareCardGroupContents';
import { ShareCardItemRow } from './ShareCardItemRow';
import ShareCardEditPublicUrl from './ShareCardEditPublicUrl';
import ShareCardEditJewelry from './ShareCardEditJewelry';
import ShareCardEditHealthEntries from './ShareCardEditHealthEntries';
import ShareCardEditContacts from './ShareCardEditContacts';
import ShareCardEditTrackables from './ShareCardEditTrackables';

import DeleteModal from '../../../components/modal/DeleteModal';
import IconFile from '../../../components/icons/IconFile';
import IconNote from '../../../components/icons/IconNote';

import List, { ListItem } from '../../../components/List';

import { isKeyboardOpen, isMobileApplication } from '../../../../utilities/MobileUtil';
import { HasLanguageCode } from '../../../../utilities/localizedUtil';
import { ShareCardColorModel } from '../../../appAuthorized/dataTransformComponents/ColorShareCard';
import {
  default as ShareCardModel,
  ShareCard,
} from '../../../../models/ShareCardModel';
import { ShareCardEditFileListItem } from '../../../components/FileListItem';
import { ShareCardEditNoteListItem } from '../../../components/NoteListItem';
import { useContactsForActiveProfile } from '../../../hooks/useContacts';
import { useFilesForActiveProfile } from '../../../hooks/useFile';
import { useNotesForActiveProfile } from '../../../hooks/useNote';
import { useShareCardForActiveProfile } from '../../../hooks/useShareCard';
import { useHealthEntriesForActiveProfile } from '../../../hooks/useHealthEntry';

const SHARE_CARD_FOOTER_HEIGHT = 74;

interface FooterProps {
  showDeleteModal: boolean;
  setShowDeleteModal(v): void;
  canChangeColor: boolean;
  canDelete: boolean;
  deleteShareCard(): void;
  onChangeColor(c): void;
  shareCardData: any;
}
const Footer: React.FC<FooterProps> = (props) => {
  const { showDeleteModal, setShowDeleteModal, onChangeColor, deleteShareCard } = props;
  const { canChangeColor, canDelete, shareCardData } = props;

  return (
    <>
      <ShareCardBottomBar>
        {
          canChangeColor
          ? (
            <ColorSelector
              shareCard={shareCardData}
              onClick={onChangeColor}
            />
          ) : <div />
        }
        {
          canDelete && <TrashShareCardButton onClick={() => setShowDeleteModal(true)} />
        }
      </ShareCardBottomBar>

      <DeleteModal
        isVisible={showDeleteModal}
        onConfirmAction={deleteShareCard}
        onCancelAction={() => setShowDeleteModal(false)}
        isAutoDismissDisabled={true}
      />
    </>
  );
};

interface Props extends HasLanguageCode {
  shareCardData: any;
  isCreate: boolean;
  shareCardColorModel: ShareCardColorModel;
  cancelTitleChange(): void;
  intl?: any;
  languageCode: string;
  updateShareCard(data: ShareCard, callback): void;
  onSubmit(scData, callback): void;
  deleteShareCard(): void;
  goToComment(): void;
  goBackToEdit(): void;
  closeShareCard(): void;
}

const ShareCardEditItems: React.FC<Props> = (props) => {
  const { onSubmit, shareCardColorModel, shareCardData, intl, isCreate, goBackToEdit, goToComment } = props;
  const { closeShareCard, updateShareCard, languageCode, deleteShareCard, cancelTitleChange } = props;

  const scrollAwareRef = React.useRef(null);
  const keyboardToggleRef = React.useRef(null);
  const [isKeyboardOpenState, setIsKeyboardOpen] = React.useState(false);
  const [state, setState] = React.useState({
    expandedCategory: null,
    showDeleteModal: false,
    isEditingTitle: false,
    saveProgress: false,
    toggleCount: 0,
    el: null,
    lastTop: 0,
    lastScroll: 0,
  });

  const [, contactActions] = useContactsForActiveProfile();
  const [{ fileList }] = useFilesForActiveProfile();
  const [{ noteList }] = useNotesForActiveProfile();
  const [, scActions] = useShareCardForActiveProfile();
  const [{ groupedHealthEntryList }] = useHealthEntriesForActiveProfile();

  keyboardToggleRef.current = () => {
    setIsKeyboardOpen(isKeyboardOpen());
  };

  React.useEffect(() => {
    if (isMobileApplication()) {
      window.addEventListener('resize', keyboardToggleRef.current);
    }

    return () => {
      if (isMobileApplication()) {
        window.removeEventListener('resize', keyboardToggleRef.current);
        keyboardToggleRef.current = null;
      }
    };
  }, []);

  React.useEffect(() => {
    if (state.toggleCount === 0 || !state.el) { return; }

    let offsetTop = 0;
    let parent = state.el.parentElement;
    while (parent) {
      if (parent.tagName === 'SECTION') {
        offsetTop = parent.offsetTop;
        break;
      }
      offsetTop = parent.offsetTop;
      parent = parent.parentElement;
    }

    if (state.lastTop > offsetTop) {
      const diff = state.lastTop - offsetTop;
      const newPos = state.lastScroll - diff;
      scrollAwareRef.current.animateScrollTo(newPos, 0);
    }

    setTimeout(() => {
      scrollAwareRef.current.animateScrollTo(offsetTop);
    }, 10);

  }, [state.toggleCount]);

  const toggleExpandCategory = (el, categoryName) => {
    const expandedCategory = categoryName === state.expandedCategory ? null : categoryName;

    setState({
      ...state,
      expandedCategory,
      toggleCount: expandedCategory ? state.toggleCount + 1 : 0,
      lastTop: el.parentElement.offsetTop,
      lastScroll: scrollAwareRef.current.scrollPos(),
      el: expandedCategory ? el : null,
    });
  };

  const shareCard = shareCardData ? scActions.instantiate(shareCardData) : null;

  if (!shareCard) { return null; }

  const { nonProviderContacts, providerContacts } = contactActions.groupContactsByProvider();
  const shareCardFileGroup = shareCard.createFileGroup(fileList);
  const shareCardNoteGroup = shareCard.createNoteGroup(noteList);
  const hasFooter = shareCard.canChangeColor() || shareCard.canDelete();
  const shareCardId = shareCardData.id;

  return (
    <ShareCardTemplate
      ref={scrollAwareRef}
      shareCardColorModel={shareCardColorModel}
      disableScrolling={state.isEditingTitle}
      scrollAwareHeader={(
        <EditableShareCardHeaderTitle
          isEditable={shareCard.canEditTitle()}
          cssColor={shareCardColorModel.cssColor(1)}
          title={shareCard.title.get(languageCode)}
          iconClass={shareCard.iconClass}
          lightIcon={shareCard.lightIcon}
          customTitle={shareCard.customTitle}
          cancelTitleChange={cancelTitleChange}
          submit={() => {
            setState({ ...state, saveProgress: true });
            onSubmit(shareCardData, closeShareCard);
          }}
          isEditingTitle={state.isEditingTitle}
          onEditTitle={(value, title) => {
            shareCard.title.set(title);
            updateShareCard(shareCard.data, () => {});
            setState({ ...state, isEditingTitle: value });
          }}
          isGroupAccessible={shareCard.isGroupAccessible()}
        />
      )}
      shareCardFooter={(
        hasFooter
        ? (
          <Footer
            shareCardData={shareCardData}
            showDeleteModal={state.showDeleteModal}
            setShowDeleteModal={(val) => { setState({ ...state, showDeleteModal: val }); }}
            canChangeColor={shareCard.canChangeColor()}
            canDelete={shareCard.canDelete()}
            deleteShareCard={deleteShareCard}
            onChangeColor={(newColor) => {
              const neShareCardData = { ...shareCardData };
              neShareCardData.color_id = newColor.id;
              updateShareCard(neShareCardData, () => {});
            }}
          />
        ) : null
      )}
      isContentLoaded={!state.saveProgress}
      shareCardId={shareCard.data.id}
    >
      <div>
        {state.isEditingTitle ? <div className="cmp-sharecardedit-overlay" /> : null}

        <div className="cmp-sharecardedititemslist">
          {
            !isCreate && (
              <ShareCardEditPublicUrl
                shareCard={shareCard}
                shareCardColorModel={shareCardColorModel}
                languageCode={languageCode}
                updateShareCard={updateShareCard}
                goBackToEdit={goBackToEdit}
              />
            )
          }

          <ShareCardEditJewelry
            shareCardColorModel={shareCardColorModel}
            shareCardId={shareCardId}
            jewelryCollection={shareCard.jewelry.collection()}
            allowsJewelry={shareCard.jewelry.isAllowed()}
            onRemove={(id) => {
              shareCard.jewelry.remove(id);
              updateShareCard(shareCard.data, () => {
                setState({ ...state, saveProgress: true });
                onSubmit(shareCard.data, () => {
                  setState({ ...state, saveProgress: false });
                });
              });
            }}
          />

          <ViewShareCardWallpaperSetup
            WrapperComponent={(props) => <ShareCardSection shareCardColorModel={shareCardColorModel}>{props.children}</ShareCardSection>}
            shareCardColorModel={shareCardColorModel}
            shareCardData={shareCardData}
            isEditMode={true}
            onEnableUrl={() => {
              const newData = ShareCardModel.toggleURL(shareCardData, true);
              updateShareCard(newData, () => {});
            }}
          />

          <ShareCardProfileGroup
            shareCardData={shareCardData}
            isExpanded={state.expandedCategory === 'profile'}
            shareCardColorModel={shareCardColorModel}
            toggleExpand={(e) => { toggleExpandCategory(e.target, 'profile'); }}
            onUpdateShareCard={(d) => {
              updateShareCard(d, () => {});
            }}
          />

          {
            shareCard.isGroupContentAllowed && (
              <ShareCardGroupContents
                shareCardData={shareCardData}
                isExpanded={state.expandedCategory === 'groupContents'}
                shareCardColorModel={shareCardColorModel}
                toggleExpand={(e) => { toggleExpandCategory(e.target, 'groupContents'); }}
                onUpdateShareCard={(d) => {
                  updateShareCard(d, () => {});
                }}
              />
            )
          }

          <ShareCardEditHealthEntries
            shareCardColorModel={shareCardColorModel}
            shareCard={shareCard}
            healthEntries={groupedHealthEntryList}
            languageCode={languageCode}
            intl={intl}
            expandedCategory={state.expandedCategory}
            toggleCategory={toggleExpandCategory}
            updateShareCard={updateShareCard}
          />

          <ShareCardEditTrackables
            shareCardColorModel={shareCardColorModel}
            shareCard={shareCard}
            languageCode={languageCode}
            intl={intl}
            expandedCategory={state.expandedCategory}
            toggleCategory={toggleExpandCategory}
            updateShareCard={updateShareCard}
          />

          <ShareCardEditContacts
            isProvider={false}
            expandedCategory={state.expandedCategory}
            shareCardColorModel={shareCardColorModel}
            contacts={nonProviderContacts}
            shareCardData={shareCardData}
            toggleExpandCategory={toggleExpandCategory}
            onUpdateShareCard={(d) => {
              updateShareCard(d, () => {});
            }}
          />

          <ShareCardEditContacts
            isProvider={true}
            expandedCategory={state.expandedCategory}
            shareCardColorModel={shareCardColorModel}
            contacts={providerContacts}
            shareCardData={shareCardData}
            toggleExpandCategory={toggleExpandCategory}
            onUpdateShareCard={(d) => {
              updateShareCard(d, () => {});
            }}
          />

          <ShareCardCategory
            showTotals={true}
            icon={() => <IconFile.Medium colorClassName="gsMedDark" />}
            group={shareCardFileGroup}
            shareCardColorModel={shareCardColorModel}
            titlePhraseData={{ id: 'list.backpackSecondaryNav.files.name', values: {} }}
            isExpanded={state.expandedCategory === 'files'}
            toggleExpand={(e) => { toggleExpandCategory(e.target, 'files'); }}
            toggleAll={() => {
              shareCardFileGroup.toggleAll();
              updateShareCard(shareCard.data, () => {});
            }}
          >
            {
              fileList.map((entry, entryIdx) => {
                const fShareCardItem = shareCard.createFileItem(entry.id);
                return (
                  <ShareCardItemRow
                    key={entryIdx}
                    cssColor={shareCardColorModel.cssColor()}
                    isExcluded={fShareCardItem.isExcluded()}
                    isRequired={fShareCardItem.isRequired()}
                    isSelected={fShareCardItem.isIncluded()}
                    onToggle={() => {
                      fShareCardItem.toggle();
                      updateShareCard(shareCard.data, () => {});
                    }}
                  >
                    <ShareCardEditFileListItem file={entry} showFileTypeName={true} hideIcon />
                  </ShareCardItemRow>
                );
              })
            }
          </ShareCardCategory>

          <ShareCardCategory
            showTotals={true}
            icon={() => <IconNote.Medium colorClassName="gsMedDark" />}
            group={shareCardNoteGroup}
            shareCardColorModel={shareCardColorModel}
            titlePhraseData={{ id: 'list.backpackSecondaryNav.notes.name', values: {} }}
            isExpanded={state.expandedCategory === 'notes'}
            toggleExpand={(e) => { toggleExpandCategory(e.target, 'notes'); }}
            toggleAll={() => {
              shareCardNoteGroup.toggleAll();
              updateShareCard(shareCard.data, () => {});
            }}
          >
            {
              noteList.map((entry, entryIdx) => {
                const nShareCardItem = shareCard.createNoteItem(entry.data.id);
                return (
                  <ShareCardItemRow
                    key={entryIdx}
                    cssColor={shareCardColorModel.cssColor()}
                    isExcluded={nShareCardItem.isExcluded()}
                    isRequired={nShareCardItem.isRequired()}
                    isSelected={nShareCardItem.isIncluded()}
                    onToggle={() => {
                      nShareCardItem.toggle();
                      updateShareCard(shareCard.data, () => {});
                    }}
                  >
                    <ShareCardEditNoteListItem note={entry} />
                  </ShareCardItemRow>
                );
              })
            }
          </ShareCardCategory>

          {shareCard.canEditComment() &&
            <List paddingSize="default">
              <ListItem onClick={goToComment}>
                <ShareCardCommentRow shareCardColorModel={shareCardColorModel} shareCard={shareCard}/>
              </ListItem>
            </List>
          }
        </div>
      </div>
    </ShareCardTemplate>
  );
};

export default injectIntl(ShareCardEditItems);
